import React, { useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowRight, KeyRound, RefreshCw, Trash2 } from 'lucide-react';
import type { Profile } from '../../../store/slices/adminSlice';
import type { AuthRole } from '../../../utils/roles';
import { PersonCell, StatusPill } from '../../../components/ui';
import { sbg } from '../../../theme/sbgTokens';
import { ROLE_SUMMARY, SETTINGS_ROLES, roleLabel } from './roles';

/** A 12-character temporary password without look-alike characters (0/O, 1/l/I). */
export const generateTempPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
  let value = '';
  for (let i = 0; i < 12; i += 1) {
    value += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return value;
};

export const displayName = (profile: Pick<Profile, 'fullName'>) => profile.fullName || 'Unnamed user';

/** Initials basis for a profile with no name: the email's local part, split on . _ - */
export const avatarBasis = (profile: Pick<Profile, 'fullName' | 'email'>) =>
  profile.fullName || (profile.email || '').split('@')[0].replace(/[._-]+/g, ' ');

/** Keeps the last non-null value so a closing dialog doesn't blank out mid-transition. */
const useLatest = <T,>(value: T | null): T | null => {
  const ref = useRef(value);
  if (value) ref.current = value;
  return ref.current;
};

const busyIcon = <CircularProgress size={16} color="inherit" aria-hidden="true" />;

/* ------------------------------------------------------------------------ */
/* Role change: one person, or a bulk selection                              */
/* ------------------------------------------------------------------------ */

export interface RoleChangeRequest {
  /** Accounts the dialog names; for a bulk change, the selected rows that are loaded. */
  profiles: Profile[];
  role: AuthRole;
  /**
   * Bulk changes go through the bulk endpoint with the ids that actually change;
   * `unchanged` counts selected accounts that already hold the role and are left alone.
   */
  bulk?: { ids: string[]; unchanged?: number };
}

/**
 * What the person acting loses by changing their own account to `next`, or null
 * when nothing drops (an HR user moving to HR or Admin loses nothing).
 */
const selfChangeWarning = (actor: AuthRole, next: AuthRole): string | null => {
  const nextLabel = roleLabel(next);
  if (SETTINGS_ROLES.includes(actor) && !SETTINGS_ROLES.includes(next)) {
    return `This includes your own account. As ${nextLabel}, you won’t be able to open Settings.`;
  }
  if (actor === 'admin' && next !== 'admin') {
    return 'This includes your own account. You’ll lose admin-only tools, such as deleting users and role preview.';
  }
  return null;
};

export const RoleChangeDialog: React.FC<{
  request: RoleChangeRequest | null;
  currentUserId?: string | null;
  /** The signed-in person's real role, so a self-change warning only names access they actually lose. */
  actorRole?: AuthRole | null;
  pending: boolean;
  error: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ request, currentUserId, actorRole, pending, error, onCancel, onConfirm }) => {
  const shown = useLatest(request);
  if (!shown) return null;
  const { profiles, role, bulk } = shown;
  const count = bulk ? bulk.ids.length : profiles.length;
  const unchanged = bulk?.unchanged ?? 0;
  const single = !bulk && profiles.length === 1 ? profiles[0] : null;
  const self = currentUserId ? profiles.find((profile) => profile.id === currentUserId) : undefined;
  const selfWarning = self ? selfChangeWarning(actorRole ?? self.role ?? 'employee', role) : null;
  const nextLabel = roleLabel(role);
  const title = single
    ? `Change ${displayName(single)}’s role to ${nextLabel}?`
    : `Change ${count} ${count === 1 ? 'user' : 'users'} to ${nextLabel}?`;
  const preview = profiles.slice(0, 5);

  return (
    <Dialog
      open={Boolean(request)}
      onClose={pending ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="role-change-title"
      aria-describedby="role-change-description"
    >
      <DialogTitle id="role-change-title">{title}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        <Typography id="role-change-description" variant="body1" sx={{ color: sbg.text.secondary }}>
          {nextLabel}: {ROLE_SUMMARY[role].toLowerCase()}. The new role decides which pages and approvals they see.
        </Typography>
        <Box
          component="ul"
          sx={{ listStyle: 'none', m: 0, p: 0, border: `1px solid ${sbg.border.default}`, borderRadius: sbg.radius.lg }}
        >
          {preview.map((profile, index) => (
            <Box
              component="li"
              key={profile.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: 2,
                py: 1.25,
                borderTop: index === 0 ? 0 : `1px solid ${sbg.border.subtle}`,
              }}
            >
              <PersonCell name={displayName(profile)} secondary={profile.email} avatarName={avatarBasis(profile)} size={24} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 'none', color: sbg.text.secondary }}>
                <StatusPill tone="neutral" noDot>{roleLabel(profile.role)}</StatusPill>
                <ArrowRight size={14} role="img" aria-label="to" />
                <StatusPill tone="info" noDot>{nextLabel}</StatusPill>
              </Box>
            </Box>
          ))}
          {count > preview.length && (
            <Box component="li" sx={{ px: 2, py: 1.25, borderTop: preview.length ? `1px solid ${sbg.border.subtle}` : 0 }}>
              <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
                {preview.length ? `and ${count - preview.length} more` : `${count} selected ${count === 1 ? 'account' : 'accounts'}`}
              </Typography>
            </Box>
          )}
        </Box>
        {unchanged > 0 && (
          <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
            {unchanged === 1 ? '1 selected user is' : `${unchanged} selected users are`} already {nextLabel} and won’t change.
          </Typography>
        )}
        {selfWarning && <Alert severity="warning">{selfWarning}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={pending} aria-busy={pending || undefined} startIcon={pending ? busyIcon : undefined}>
          {pending ? 'Changing…' : single || count === 1 ? 'Change role' : `Change ${count} roles`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------------ */
/* Reset password                                                            */
/* ------------------------------------------------------------------------ */

export const ResetPasswordDialog: React.FC<{
  target: Profile | null;
  password: string;
  onPasswordChange: (value: string) => void;
  pending: boolean;
  error: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ target, password, onPasswordChange, pending, error, onCancel, onConfirm }) => {
  const shown = useLatest(target);
  const [touched, setTouched] = React.useState(false);
  React.useEffect(() => {
    if (target) setTouched(false);
  }, [target]);
  if (!shown) return null;
  const missing = touched && !password;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!password || pending) return;
    onConfirm();
  };

  return (
    <Dialog
      open={Boolean(target)}
      onClose={pending ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="reset-password-title"
    >
      <Box component="form" onSubmit={submit} noValidate sx={{ display: 'contents' }}>
        <DialogTitle id="reset-password-title">Reset password</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2 }}>
          <PersonCell name={displayName(shown)} secondary={shown.email} avatarName={avatarBasis(shown)} />
          <Typography variant="body1" sx={{ color: sbg.text.secondary }}>
            Set a temporary password for this account, then share it with them directly.
          </Typography>
          <Box sx={{ display: 'grid', gap: 1 }}>
            <TextField
              label="Temporary password"
              fullWidth
              autoFocus
              value={password}
              onChange={(event) => onPasswordChange(event.target.value)}
              error={missing}
              helperText={missing ? 'Enter a password or generate one.' : undefined}
              slotProps={{ htmlInput: { autoComplete: 'off', spellCheck: false } }}
            />
            <Box>
              <Button variant="outlined" size="small" startIcon={<RefreshCw />} onClick={() => onPasswordChange(generateTempPassword())}>
                Generate password
              </Button>
            </Box>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={pending} aria-busy={pending || undefined} startIcon={pending ? busyIcon : <KeyRound />}>
            {pending ? 'Resetting…' : 'Reset password'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

/* ------------------------------------------------------------------------ */
/* Delete user                                                               */
/* ------------------------------------------------------------------------ */

export const DeleteUserDialog: React.FC<{
  target: Profile | null;
  pending: boolean;
  canConfirm: boolean;
  error: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ target, pending, canConfirm, error, onCancel, onConfirm }) => {
  const shown = useLatest(target);
  // Name the account being deleted (book §11.14): "Delete Maria Chen’s account?"
  const who = shown ? shown.fullName || shown.email : null;
  return (
    <Dialog
      open={Boolean(target)}
      onClose={onCancel}
      disableEscapeKeyDown={pending}
      maxWidth="sm"
      fullWidth
      aria-labelledby="delete-user-title"
      aria-describedby="delete-user-description"
    >
      <DialogTitle id="delete-user-title">{who ? `Delete ${who}’s account?` : 'Delete this account?'}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        {shown && (
          <PersonCell name={displayName(shown)} secondary={shown.email} avatarName={avatarBasis(shown)} size={40} />
        )}
        <Typography id="delete-user-description" variant="body1" sx={{ color: sbg.text.secondary }}>
          This permanently deletes their login account and access profile. Their employee record and attendance history
          will remain. Any employee or manager links to this account will be removed. This cannot be undone.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onCancel} disabled={pending} autoFocus>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={pending || !canConfirm}
          aria-busy={pending || undefined}
          startIcon={pending ? busyIcon : <Trash2 />}
        >
          {pending ? 'Deleting…' : 'Delete user'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
